package ai.beakman.undercover.data

import ai.beakman.undercover.model.WordHint

/**
 * Textos dos temas em português (pt-BR). Fonte da verdade do conteúdo do jogo.
 *
 * [NAMES]: themeId -> nome do tema. [WORDS]: wordId -> palavra + dica.
 * As chaves são as MESMAS nos três idiomas (ver [ThemeCatalog]).
 *
 * Padrão das dicas: VAGAS, estilo "categoria", curtas, e REPETEM entre 2-3 palavras
 * do mesmo tema (ex.: "Vive na água" cobre Golfinho+Tubarão).
 */
object ThemeStrings_pt {
    val NAMES: Map<String, String> = mapOf(
        "animais" to "Animais",
        "comidas" to "Comidas",
        "profissoes" to "Profissões",
        "filmes" to "Filmes",
        "lugares" to "Lugares",
        "esportes" to "Esportes",
    )

    val WORDS: Map<String, WordHint> = mapOf(
        // animais
        "animais_1" to WordHint("Leão", "Tem pelo"),
        "animais_2" to WordHint("Elefante", "É bem grande"),
        "animais_3" to WordHint("Golfinho", "Vive na água"),
        "animais_4" to WordHint("Tubarão", "Vive na água"),
        "animais_5" to WordHint("Águia", "Sabe se mover rápido"),
        "animais_6" to WordHint("Cavalo", "Anda com quatro patas"),
        "animais_7" to WordHint("Cachorro", "Aparece bastante em casa"),
        "animais_8" to WordHint("Gato", "Aparece bastante em casa"),
        "animais_9" to WordHint("Panda", "Tem cor marcante"),
        "animais_10" to WordHint("Canguru", "Vive longe daqui"),
        "animais_11" to WordHint("Pinguim", "Vive em clima específico"),
        "animais_12" to WordHint("Girafa", "Chama atenção pelo corpo"),

        // comidas
        "comidas_1" to WordHint("Pizza", "Vem de fora"),
        "comidas_2" to WordHint("Hambúrguer", "Vem de fora"),
        "comidas_3" to WordHint("Sushi", "Vem de fora"),
        "comidas_4" to WordHint("Feijoada", "É bem nossa"),
        "comidas_5" to WordHint("Lasanha", "Aparece em jantares"),
        "comidas_6" to WordHint("Tapioca", "É bem nossa"),
        "comidas_7" to WordHint("Pastel", "Costuma ser de feira"),
        "comidas_8" to WordHint("Açaí", "Servido bem frio"),
        "comidas_9" to WordHint("Brigadeiro", "Aparece em festas"),
        "comidas_10" to WordHint("Coxinha", "Costuma ser de feira"),
        "comidas_11" to WordHint("Churrasco", "Combina com fim de semana"),
        "comidas_12" to WordHint("Salada", "Lembra leveza"),

        // profissoes
        "profissoes_1" to WordHint("Médico", "Lida com pessoas"),
        "profissoes_2" to WordHint("Professor", "Lida com pessoas"),
        "profissoes_3" to WordHint("Bombeiro", "Trabalho de risco"),
        "profissoes_4" to WordHint("Policial", "Trabalho de risco"),
        "profissoes_5" to WordHint("Engenheiro", "Precisa estudar muito"),
        "profissoes_6" to WordHint("Advogado", "Precisa estudar muito"),
        "profissoes_7" to WordHint("Chef", "Trabalha com as mãos"),
        "profissoes_8" to WordHint("Piloto", "Trabalha viajando"),
        "profissoes_9" to WordHint("Dentista", "Lida com pessoas"),
        "profissoes_10" to WordHint("Jornalista", "Trabalha com informação"),
        "profissoes_11" to WordHint("Veterinário", "Lida com seres vivos"),
        "profissoes_12" to WordHint("Arquiteto", "Trabalha criando coisas"),

        // filmes
        "filmes_1" to WordHint("Titanic", "Já fez muito sucesso"),
        "filmes_2" to WordHint("Matrix", "Tem continuação"),
        "filmes_3" to WordHint("Vingadores", "Tem continuação"),
        "filmes_4" to WordHint("Frozen", "Pode ver com criança"),
        "filmes_5" to WordHint("Senhor dos Anéis", "É bem longo"),
        "filmes_6" to WordHint("Coringa", "Já fez muito sucesso"),
        "filmes_7" to WordHint("Avatar", "Conhecido pelos efeitos"),
        "filmes_8" to WordHint("Velozes e Furiosos", "Tem continuação"),
        "filmes_9" to WordHint("Harry Potter", "Pode ver com criança"),
        "filmes_10" to WordHint("Toy Story", "Pode ver com criança"),
        "filmes_11" to WordHint("Jurassic Park", "Conhecido pelos efeitos"),
        "filmes_12" to WordHint("Rocky", "Tem continuação"),

        // lugares
        "lugares_1" to WordHint("Praia", "Lugar ao ar livre"),
        "lugares_2" to WordHint("Montanha", "Lugar ao ar livre"),
        "lugares_3" to WordHint("Floresta", "Lugar ao ar livre"),
        "lugares_4" to WordHint("Deserto", "Lugar ao ar livre"),
        "lugares_5" to WordHint("Cidade", "Tem muita gente"),
        "lugares_6" to WordHint("Escola", "Vai todo dia"),
        "lugares_7" to WordHint("Hospital", "Ninguém quer ficar muito tempo"),
        "lugares_8" to WordHint("Shopping", "Tem muita gente"),
        "lugares_9" to WordHint("Estádio", "Tem muita gente"),
        "lugares_10" to WordHint("Aeroporto", "Lugar de passagem"),
        "lugares_11" to WordHint("Museu", "Lugar para visitar"),
        "lugares_12" to WordHint("Parque", "Lugar ao ar livre"),

        // esportes
        "esportes_1" to WordHint("Futebol", "Joga em grupo"),
        "esportes_2" to WordHint("Basquete", "Joga em grupo"),
        "esportes_3" to WordHint("Vôlei", "Joga em grupo"),
        "esportes_4" to WordHint("Tênis", "Tem equipamento próprio"),
        "esportes_5" to WordHint("Natação", "Exige bastante fôlego"),
        "esportes_6" to WordHint("Surf", "Depende do clima"),
        "esportes_7" to WordHint("Skate", "Tem equipamento próprio"),
        "esportes_8" to WordHint("Boxe", "Tem contato físico"),
        "esportes_9" to WordHint("Ciclismo", "Tem equipamento próprio"),
        "esportes_10" to WordHint("Corrida", "Exige bastante fôlego"),
        "esportes_11" to WordHint("Golfe", "Tem equipamento próprio"),
        "esportes_12" to WordHint("Handebol", "Joga em grupo"),
    )
}
